import { serviceTypeBadge } from '@/components/features/offerings/common';
import { LoaderElement } from '@/components/features/shared/LoaderElement';
import { NavHeader } from '@/components/features/shared/NavHeader';
import { UnexpectedError } from '@/components/features/shared/UnexpectedError';
import { getSubscriptionLabel, PriceLabelWithTaxResponsive, renderSinglePeriod } from '@/components/features/shared/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Agreement } from '@/domain/agreements/agreement';
import { CollectorStatus } from '@/domain/collectors/collector';
import { BillingType } from '@/domain/invoices/invoice';
import { Offering, ServiceType } from '@/domain/offerings/offering';
import { AgreementPayment, PaymentMethod } from '@/domain/payments/payments';
import { useAgreement } from '@/hooks/api/useAgreements';
import { useCollectors } from '@/hooks/api/useCollectors';
import { useOffering } from '@/hooks/api/useOfferings';
import { useParcelByContract } from '@/hooks/api/useParcels';
import { useAgreementCustomer, useMultiPaymentData } from '@/hooks/api/usePayments';
import { isNullOrEmpty } from '@/utils/validations';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { CustomerData } from './CustomerData';
import { ParcelContractData } from './ParcelContractData';
import { PaymentAccreditationForm, paymentFormSchema, type PaymentFormValues } from './PaymentAccreditationForm';
import { SubtotalsData } from './SubtotalsData';
import { useOnSubmit } from './useOnSubmit';
import { PageContainer } from '@/components/features/shared/PageContainer';

export const MultiPayments = ({ paymentId }: { paymentId: string }) => {
  const { data: agreementPayments, isLoading: isLoadingPaymentData } = useMultiPaymentData(paymentId);
  const mainContractId = agreementPayments?.find(ap => !isNullOrEmpty(ap.mainContractId))?.mainContractId;

  const { data: agreement, isLoading: isLoadingAgreement } = useAgreement(mainContractId || '');
  const { data: offering, isLoading: isLoadingOffering } = useOffering(agreement?.offeringId || '');

  // console.log(agreementPayments)

  if (isLoadingPaymentData || isLoadingAgreement || isLoadingOffering)
    return <LoaderElement />

  if (!agreementPayments || !agreement || !offering)
    return <UnexpectedError />

  return <MultiPaymentsCmp paymentId={paymentId} mainAgreement={agreement} agreementPayments={agreementPayments} offering={offering} />;
}

export const MultiPaymentsCmp = ({ paymentId, mainAgreement, agreementPayments, offering }
  : { paymentId: string; mainAgreement: Agreement, agreementPayments: AgreementPayment[], offering: Offering }) => {

  const { data: customer, isLoading: isLoadingCustomer } = useAgreementCustomer(mainAgreement.id);
  const { data: collectors = [], isLoading: isLoadingCollectors } = useCollectors({ status: CollectorStatus.Active });
  const { data: parcel, isLoading: isLoadingParcel } = useParcelByContract({
    contractId: mainAgreement.id,
    enabled: offering?.serviceType === ServiceType.PARCELAS,
  });

  const isLoading = isLoadingCustomer || isLoadingCollectors || isLoadingParcel;

  const form = useForm<PaymentFormValues>({
    resolver: zodResolver(paymentFormSchema),
    defaultValues: {
      billingType: offering?.isInvoiceable ? BillingType.Invoice : BillingType.Receipt,
      paymentMethod: PaymentMethod.BANK_TRANSFER,
      paymentDetails: '',
      paymentVerified: false,
      partialPayment: false,
      amount: undefined,
      interestAmount: undefined,
      collectorId: (mainAgreement.collectorId || null) as string | null,
    },
  });

  const isPartialPayment = form.watch('partialPayment');
  const amount = form.watch('amount');
  const interestAmount = form.watch('interestAmount');
  const selectedCollectorId = form.watch('collectorId');

  const hasContractCollector = !!mainAgreement.collectorId;
  const selectedDifferentCollector = !!(hasContractCollector && selectedCollectorId && selectedCollectorId !== mainAgreement.collectorId);
  const assignedCollectorName = hasContractCollector && mainAgreement.collectorId
    ? collectors.find(c => c.id === mainAgreement.collectorId)?.name
    : null;

  const totalAmountToBePayed = agreementPayments.reduce((total, ap) => total + (ap.totalAmount || 0), 0);
  const amountToCharge = ((isPartialPayment ? amount : totalAmountToBePayed) || 0) + (interestAmount ?? 0);
  const partialPaymentsEnabled = false;
  const amountToInvoice = amountToCharge

  const { onSubmit, isPending } = useOnSubmit({ contractId: mainAgreement.id, paymentId, amountToCharge, customer });

  if (isLoading) {
    return <LoaderElement />
  }

  return (
    <PageContainer className='max-w-4xl'>
      <NavHeader>
        <h1 className="text-2xl font-bold tracking-tight">
          Confirmación del pago y emisión de comprobante
        </h1>
        <p className='text-muted-foreground'>Complete los datos para confirmar la recepción del pago y emitir un comprobante</p>
      </NavHeader>

      <div className="grid grid-cols-1 gap-4 mt-6">
        <Card className="h-full border-gray-200 shadow-sm flex flex-col overflow-hidden">
          <div className="bg-primary text-primary-foreground p-6">
            <h2 className="text-lg font-semibold flex items-center justify-between">
              <span>Resumen de Operación</span>
              <span className="text-xs font-mono bg-white/20 px-2 py-1 rounded">{mainAgreement?.contractNumber}</span>
            </h2>
          </div>

          <CardContent className="p-0 flex-1 flex flex-col bg-white">
            <CustomerData customer={customer} />

            <div className="p-6 border-b border-gray-100 flex-1">
              <div className='flex items-center gap-10 mb-5'>
                <div>
                  <h3 className="text-xs font-bold text-primary mb-3 uppercase tracking-wider">
                    Tipo de servicio a cobrar
                  </h3>
                  <div className="flex flex-wrap items-center gap-3 mb-4">
                    {offering.serviceType && <>{serviceTypeBadge(offering.serviceType)}</>}
                  </div>
                </div>
                <div className="flex-1">
                  {parcel && (
                    <ParcelContractData parcel={parcel} />
                  )}
                </div>
              </div>

              <div className="space-y-3">
                <div className="grid grid-cols-[2fr_6fr_3fr] items-center text-[10px] font-bold text-primary uppercase tracking-wider border-b border-gray-100 pb-2">
                  <span>Rubro</span>
                  <span>Concepto</span>
                  <span>Monto</span>
                </div>

                {agreementPayments.map((item, idx) => (
                  <div key={idx} className="grid grid-cols-[2fr_6fr_3fr] items-center">
                    <div className="w-fit">
                      <span className="text-sm font-medium text-gray-900 leading-snug">
                        {item.offeringCode}
                      </span>
                    </div>
                    <div className="pr-4 flex-1">
                      <div className="text-sm font-medium text-gray-900 leading-snug">
                        {item.offeringName}
                        {item.periodStartDate && item.periodEndDate && (
                          <span className="ml-1 font-semibold font-mono">
                            {getSubscriptionLabel(item.periodStartDate, item.periodEndDate, item.billingFrequency)}
                          </span>
                        )}
                        {item.paymentPeriodFrom && item.periodStartDate?.getTime() !== item.paymentPeriodFrom.getTime() && (
                          <span className="ml-1 font-semibold">
                            - {renderSinglePeriod(item.paymentPeriodFrom)}
                          </span>
                        )}
                        {item.description && (
                          <span className="ml-1">
                            - {item.description}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                      <PriceLabelWithTaxResponsive price={item.totalAmount || 0} taxRate={item.taxRate} />
                    </div>
                  </div>
                ))
                }
              </div>
            </div>

            <SubtotalsData amountOwed={0} amountPayed={0} interestAmount={interestAmount} amountToCharge={amountToCharge} />
          </CardContent>
        </Card>

        <Card className="h-full border-gray-200 shadow-sm">
          <CardHeader className="bg-white pb-4 border-b">
            <CardTitle className="text-xl">Acreditación del pago</CardTitle>
            <CardDescription>Complete la información del pago a recibir por parte del cliente</CardDescription>
          </CardHeader>
          <CardContent className="pt-6 bg-white">
            <PaymentAccreditationForm
              form={form}
              onSubmit={onSubmit}
              isPending={isPending}
              partialPaymentsEnabled={partialPaymentsEnabled}
              maxPartialAmount={totalAmountToBePayed}
              amountToInvoice={amountToInvoice}
              amountToCharge={amountToCharge}
              activeCollectors={collectors}
              assignedCollectorName={assignedCollectorName}
              selectedDifferentCollector={selectedDifferentCollector}
              amountOwed={0}
              contractId={mainAgreement.id}
            />
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
